import { AssetTableItem } from "../../../types/types";

/** Flatten a nested asset hierarchy into a flat array. */
export function flattenAssets(assets: AssetTableItem[]): AssetTableItem[] {
  const result: AssetTableItem[] = [];
  function traverse(items: AssetTableItem[]) {
    for (const item of items) {
      result.push(item);
      if (item.children) traverse(item.children);
    }
  }
  traverse(assets);
  return result;
}

/** Return the full ancestor path (inclusive of target) for a given asset ID, or []. */
export function findAncestors(assets: AssetTableItem[], targetId: number): AssetTableItem[] {
  function search(items: AssetTableItem[], path: AssetTableItem[]): AssetTableItem[] | null {
    for (const item of items) {
      if (item.id === targetId) return [...path, item];
      if (item.children) {
        const found = search(item.children, [...path, item]);
        if (found) return found;
      }
    }
    return null;
  }
  return search(assets, []) ?? [];
}

/** Return true if the node's name / category / tags match the filter, or if any descendant does.
 *  The optional `typeFilter` restricts to a specific asset type. */
export function nodeMatchesFilter(
  node: AssetTableItem,
  query: string,
  typeFilter?: string
): boolean {
  if (!query && !typeFilter) return true;

  const matchesQuery =
    !query ||
    (() => {
      const q = query.toLowerCase();
      if (node.name.toLowerCase().includes(q)) return true;
      if (node.category?.toLowerCase().includes(q)) return true;
      if (node.tags?.some((t) => t.toLowerCase().includes(q))) return true;
      return false;
    })();

  const matchesType = !typeFilter || node.type === typeFilter;

  if (matchesQuery && matchesType) return true;

  // A parent is visible when at least one descendant passes both filters
  if (node.children?.some((child) => nodeMatchesFilter(child, query, typeFilter))) return true;

  return false;
}

/** Collect all IDs in the hierarchy into a Set. */
export function collectAllIds(assets: AssetTableItem[]): Set<number> {
  const ids = new Set<number>();
  function traverse(items: AssetTableItem[]) {
    for (const item of items) {
      ids.add(item.id);
      if (item.children) traverse(item.children);
    }
  }
  traverse(assets);
  return ids;
}

/**
 * Return the IDs of `nodeId` itself plus all its descendants.
 * Used to build the set of invalid drop targets when dragging a node.
 */
export function getSubtreeIds(assets: AssetTableItem[], nodeId: number): Set<number> {
  const result = new Set<number>();

  function collectDown(items: AssetTableItem[]) {
    for (const item of items) {
      result.add(item.id);
      if (item.children) collectDown(item.children);
    }
  }

  function findAndCollect(items: AssetTableItem[]): boolean {
    for (const item of items) {
      if (item.id === nodeId) {
        result.add(item.id);
        collectDown(item.children ?? []);
        return true;
      }
      if (item.children && findAndCollect(item.children)) return true;
    }
    return false;
  }

  findAndCollect(assets);
  return result;
}
